// Top up a card from the Vault balance.
//
// Reached two ways, both landing here:
//   /topup/from-vault?cardId=…&currencyId=…   (from the card top-up screen)
//   /topup/from-vault?vaultCurrency=TRX_USDT  (from Vault → "Top up card")
//
// The bot resolves the authoritative destination + currency server-side; this
// screen only collects the card (fixed or picked) and the amount, then submits
// with a stable idempotency key so a retry never double-sends.

import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import {
  getWalletBalances,
  listCards,
  listCurrencies,
  topUpFromVault,
  type CardSummary,
} from '../api/client.ts';
import { IconChevronLeft } from '../components/icons.tsx';
import { ErrorState } from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { applyPendingSends } from '../lib/pending-sends.ts';
import { cardCurrencyToVault, newIdempotencyKey } from '../lib/vault-currency.ts';

import { rootRoute } from './__root.tsx';

export interface TopupFromVaultSearch {
  readonly cardId?: string;
  readonly currencyId?: string;
  readonly vaultCurrency?: string;
}

// The platform floor mirrors the card top-up screen: never let the user submit
// less than the platform accepts.
const MIN_TOPUP = 10;
const flooredMinimum = (raw: string | undefined): number => {
  const n = Number(raw);
  return Number.isFinite(n) && n > MIN_TOPUP ? n : MIN_TOPUP;
};

const cardLabel = (card: CardSummary): string =>
  `•••• ${card.lastFour ?? '????'}${card.cardLabel !== undefined ? ` · ${card.cardLabel}` : ''}`;

export const TopupFromVaultPage = (): ReactNode => {
  const navigate = useNavigate();
  const search = topupFromVaultRoute.useSearch();
  // One key per screen mount, reused on retry.
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [amount, setAmount] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<string>(search.cardId ?? '');

  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: ({ signal }) => listCards(signal) });
  const currenciesQ = useQuery({
    queryKey: ['currencies'],
    queryFn: ({ signal }) => listCurrencies(signal),
  });
  const balancesQ = useQuery({
    queryKey: ['wallet-balances'],
    queryFn: ({ signal }) => getWalletBalances(signal),
    staleTime: 30_000,
    select: applyPendingSends,
  });

  const sendM = useMutation({
    mutationFn: (input: { cardId: string; currencyId: string }) =>
      topUpFromVault(input.cardId, input.currencyId, amount, idempotencyKey),
    onSuccess: () => {
      showToast('Top-up sent');
      window.setTimeout(() => {
        void navigate({ to: '/' });
      }, 900);
    },
  });

  const goBack = (): void => {
    if (search.cardId !== undefined) {
      void navigate({ to: '/topup', search: { cardId: search.cardId } });
    } else {
      void navigate({ to: '/vault' });
    }
  };

  if (cardsQ.isLoading || currenciesQ.isLoading || balancesQ.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 0' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }} aria-hidden="true" />
      </div>
    );
  }
  if (cardsQ.isError || currenciesQ.isError || balancesQ.isError) {
    return (
      <ErrorState
        message="Could not load top-up data"
        onRetry={(): void => {
          void cardsQ.refetch();
          void currenciesQ.refetch();
          void balancesQ.refetch();
        }}
      />
    );
  }

  const currencies = currenciesQ.data?.currencies ?? [];
  // Resolve the card-platform currency: by id (from the top-up flow) or by
  // matching the Vault currency (from the Vault entry point).
  const cardCurrency =
    search.currencyId !== undefined
      ? currencies.find((c) => c.id === search.currencyId)
      : currencies.find((c) => cardCurrencyToVault(c.symbol, c.network) === search.vaultCurrency);
  const currencyId = cardCurrency?.id;
  const vaultCurrency =
    cardCurrency !== undefined
      ? cardCurrencyToVault(cardCurrency.symbol, cardCurrency.network)
      : (search.vaultCurrency ?? null);
  const token = cardCurrency?.symbol ?? vaultCurrency ?? '—';

  const cards = cardsQ.data?.cards ?? [];
  const selectedCard = cards.find((c) => c.id === selectedCardId);
  const vaultBalance = balancesQ.data?.balances.find((b) => b.currency === vaultCurrency);
  const available = Number(vaultBalance?.amount ?? '0');
  const minimum = flooredMinimum(cardCurrency?.minimumAmount);

  const numAmount = Number(amount);
  const amountValid =
    amount !== '' && Number.isFinite(numAmount) && numAmount >= minimum && numAmount <= available;
  const canSend =
    currencyId !== undefined && selectedCardId !== '' && amountValid && !sendM.isPending;

  // The currency exists but the vault can't fund it — dead end, send the user back.
  if (cardCurrency === undefined || vaultCurrency === null) {
    return (
      <ErrorState message="This currency is not available for a Vault top-up" onRetry={goBack} />
    );
  }

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button type="button" className="topbar-back" aria-label="Back" onClick={goBack}>
          <IconChevronLeft />
        </button>
        <p className="eyebrow">Top up from Vault</p>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      {/* Destination card: fixed when arriving from the top-up flow, otherwise picked here. */}
      <div className="section-header fade-in d1" style={{ marginTop: 0 }}>
        <p className="eyebrow">To card</p>
      </div>
      <div className="hairline fade-in d1" style={{ marginTop: 8, marginBottom: 4 }} />
      {search.cardId !== undefined ? (
        <div className="topup-target-card fade-in d1" style={{ padding: '16px 0' }}>
          <p className="currency-name">
            {selectedCard !== undefined ? cardLabel(selectedCard) : '••••'}
          </p>
        </div>
      ) : cards.length === 0 ? (
        <div className="empty-state">No cards available</div>
      ) : (
        <div className="fade-in d1">
          {cards.map((c, i) => (
            <div key={c.id}>
              <button
                type="button"
                className="currency-row"
                aria-pressed={selectedCardId === c.id}
                onClick={(): void => {
                  setSelectedCardId(c.id);
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="currency-name">{cardLabel(c)}</p>
                </div>
                <span
                  aria-hidden="true"
                  style={{ marginLeft: 10, color: 'var(--accent)', fontSize: 18, lineHeight: 1 }}
                >
                  {selectedCardId === c.id ? '●' : '○'}
                </span>
              </button>
              {i < cards.length - 1 ? <div className="hairline" /> : null}
            </div>
          ))}
        </div>
      )}

      {/* Amount */}
      <div className="section-header fade-in d2" style={{ marginTop: 28 }}>
        <p className="eyebrow">Amount</p>
        <p className="status-line">
          Vault: {vaultBalance?.amount ?? '0'} {token}
        </p>
      </div>
      <div className="hairline fade-in d2" style={{ marginTop: 8, marginBottom: 14 }} />

      <div className="address-frame fade-in d2">
        <div className="address-row">
          <input
            className="amount-input"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e): void => {
              // Keep it a plain decimal string — the value crosses the wire as-is.
              const v = e.target.value.replace(/[^0-9.]/g, '');
              setAmount(v);
            }}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 20,
              outline: 'none',
            }}
            aria-label={`Amount in ${token}`}
          />
          <span style={{ color: 'var(--text-secondary)', fontSize: 15 }}>{token}</span>
        </div>
      </div>

      <p
        role="note"
        style={{
          fontSize: 12,
          color: 'var(--text-tertiary)',
          marginBottom: 20,
          lineHeight: 1.55,
        }}
      >
        Minimum {minimum} {token}. Available in Vault: {vaultBalance?.amount ?? '0'} {token}.
      </p>

      {sendM.isError ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger, #c0392b)', marginBottom: 14 }}>
          {sendM.error instanceof Error ? sendM.error.message : 'Top-up failed'}
        </p>
      ) : null}

      <button
        type="button"
        className="btn-primary"
        disabled={!canSend}
        onClick={(): void => {
          if (currencyId === undefined || selectedCardId === '') return;
          sendM.mutate({ cardId: selectedCardId, currencyId });
        }}
      >
        {sendM.isPending ? 'Sending…' : 'Send'}
      </button>
    </main>
  );
};

export const topupFromVaultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/topup/from-vault',
  component: TopupFromVaultPage,
  validateSearch: (s: Record<string, unknown>): TopupFromVaultSearch => ({
    ...(typeof s.cardId === 'string' ? { cardId: s.cardId } : {}),
    ...(typeof s.currencyId === 'string' ? { currencyId: s.currencyId } : {}),
    ...(typeof s.vaultCurrency === 'string' ? { vaultCurrency: s.vaultCurrency } : {}),
  }),
});
